// client/src/pages/knowledgeSharing.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { GlobalContext } from '../../context/provider';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomAlert from '../../components/customs/customAlert';
import useBlobStorage from '../../hooks/useBlobStorage';
import useCognitive from '../../hooks/useCognitive';

import './knowledgeSharing.css';
import '../../styles/globalstyles.css';

const KnowledgeSharing = () => {
  const location = useLocation();

  const [sortAscending, setSortAscending] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  const { authState } = useContext(GlobalContext);
  const { authData } = authState;

  const {
    knowledgePosts,
    isKnowledgeLoaded,
    knowledgePostsCount,
    listKnowledgePosts,
    loadKnowledgePosts,
    updateKnowledgePost,
    deleteKnowledgePost,
  } = useBlobStorage();

  const { 
    // speakTextAzure, 
    speakText, 
    pauseSpeech, 
    stopSpeech, 
    isMuted, 
    toggleMute 
  } = useCognitive();

  // Update the useEffect (line 56-60)
  useEffect(() => {
    if (selectedPost?.content && !isMuted) {
      // speakTextAzure(selectedPost.content);
      speakText(selectedPost.content, 'nova', 'tts-1');
    }
  }, [selectedPost, isMuted, speakText]);

  // Check if user is admin
  const isAdmin = authData?.role === 'admin';

  // Load knowledge posts on mount
  useEffect(() => {
    const loadPosts = async () => {
      const result = await listKnowledgePosts();
      if (result.success && result.number > 0) {  // ← Change 'count' to 'number'
        await loadKnowledgePosts(result.number);  // ← Change 'count' to 'number'
      }
    };
    loadPosts();
  }, []);

  useEffect(() => {
    if (selectedPost?.content && !isMuted) {
      // speakTextAzure(selectedPost.content, 'en-US-JennyNeural', 'en-US');
      speakText(selectedPost.content, 'nova', 'tts-1');
    }
  }, [selectedPost, isMuted, speakText]);


  // Parse and sort posts
  const sortedPosts = Array.isArray(knowledgePosts)
    ? [...knowledgePosts]
        .map((post) => {
          const [subjectLine, ...bodyLines] = post.content.split('\n\n');
          const subject = subjectLine?.replace('Subject: ', '').trim();
          const content = bodyLines.join('\n\n').trim();

          return {
            ...post,
            subject,
            content,
          };
        })
        .sort((a, b) => {
          const timeA = new Date(a.timestamp).getTime();
          const timeB = new Date(b.timestamp).getTime();
          return sortAscending ? timeA - timeB : timeB - timeA;
        })
    : [];

  const getPreview = (text) => {
    const firstLine = text.split('\n')[0];
    return firstLine.length > 60 ? firstLine.slice(0, 60) + '...' : firstLine;
  };

  const handleUpdatePost = async () => {
    if (!selectedPost) return;

    const message = `Subject: ${selectedPost.subject}\n\n${selectedPost.content}`;
    const result = await updateKnowledgePost(
      authData.id,
      selectedPost.filename,
      { message }
    );

    if (result.success) {
      setSelectedPost(null);
      setIsEditing(false);
    }
  };

  const handleDeletePost = async (fileName) => {
    const result = await deleteKnowledgePost(authData.id, fileName);
    if (result.success) {
      setSelectedPost(null);
    }
  };

  const handlePostClick = (post) => {
    setSelectedPost(post);
  };

  const charCount = selectedPost?.content?.length || 0;
  const maxChars = 2000;
  const charPercentage = (charCount / maxChars) * 100;
  const isAuthorized = authData?.role === 'admin';

  const profileHeaderData = { 
    ...authData, 
    title: '📚 IoT Knowledge Base',
  };

  return (
    <div className="main-container">
      <div className="main-content">
        {/* Header */}
        <div className="header-row">
          <ProfileHeader data={profileHeaderData} activePage="knowledge" />
          <div className="headerMiddle">
            <img src="/images/logo.png" alt="Platform Logo" className="header-logo" />
          </div>
          {isAuthorized ? ( 
            <SharedHeader notificationCount={0} icons={['writenote']} />
          ) : (
            <SharedHeader notificationCount={0} icons={[]} />
          )
          }
        </div>

        <div className="page-title-bar">
          <button
            onClick={() => setSortAscending((prev) => !prev)}
            className="sort-button"
          >
            {sortAscending ? '📅 Oldest First' : '📅 Newest First'}
          </button>
        </div>

        {/* Posts List */}
        {!isKnowledgeLoaded ? (
          <div className="loading-container">
            <p>Loading knowledge posts...</p>
          </div>
        ) : knowledgePosts.length > 0 ? (
          <div className="scrollable-list">
            {sortedPosts.map((post, idx) => (
              <div
                key={idx}
                className="course-card"
                onClick={() => handlePostClick(post)}
              >
                <div className="knowledge-post-header">
                  <div className="post-icon">📖</div>
                  <div className="post-meta">
                    <h4 className="post-title">{getPreview(post.subject)}</h4>
                    <p className="post-timestamp">
                      📅 {new Date(post.timestamp).toLocaleDateString()} • 
                      ⏰ {new Date(post.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>

                <div className="knowledge-post-content">
                  <p className="post-preview">{getPreview(post.content)}</p>
                </div>

                <div className="knowledge-post-footer">
                  <span className="read-more">Read more →</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-items">
            <p>📭 No knowledge posts available</p>
            <p className="hint">Check back later for IoT tips and updates</p>
          </div>
        )}

        {/* Post Detail Modal */}
        {selectedPost && (
          <div className="modal-overlay">
            <div className="modal-content-top">
              <div className="modal-header-top">
                <h2>📚 Knowledge Post</h2>
                <div className="modal-actions-group">
                  {isAdmin && (
                    <>
                      <button
                        className={`ks-modal-action-btn edit-btn ${isEditing ? 'active' : ''}`}
                        onClick={() => {
                          if (!isEditing) {
                            pauseSpeech();
                            // setIsMuted(true);
                          }
                          setIsEditing((prev) => !prev);
                        }}
                        title={isEditing ? 'Cancel editing' : 'Edit post'}
                      >
                        {isEditing ? '🚫' : '✏️'}
                      </button>
                      <button
                        className="ks-modal-action-btn delete-btn"
                        onClick={() => {
                          setPendingDeleteId(selectedPost.filename);
                          setShowDeleteAlert(true);
                        }}
                        title="Delete post"
                      >
                        🗑️
                      </button>
                    </>
                  )}
                  <button
                    className={`ks-modal-action-btn speak-btn ${!isMuted ? 'speaking' : ''}`}
                    // onClick={() => {
                    //   const newMuted = !isMuted;
                    //   setIsMuted(newMuted);
                    //   if (newMuted) pauseSpeech();
                    // }}
                    onClick={toggleMute}
                    title={isMuted ? 'Start speaking' : 'Pause speaking'}
                  >
                    {isMuted ? '🔇' : '🔊'}
                  </button>
                  <button
                    className="modal-close-btn"
                    onClick={() => {
                      stopSpeech();
                      setSelectedPost(null);
                      // setIsMuted(true);
                      setIsEditing(false);
                    }}
                  >
                    ✖
                  </button>
                </div>
              </div>

              <div className="modal-body">
                {/* Quick Stats */}
                <div className="post-stat-box">
                  <div className="post-stat-icon">📝</div>
                  <div className="post-stat-info">
                    <p className="post-stat-label">Words</p>
                    <p className="post-stat-value">
                      {selectedPost.content.split(/\s+/).filter((w) => w).length}
                    </p>
                  </div>
                </div>

                {/* Post Content */}
                <div className="knowledge-post-detail-content">
                  {isEditing ? (
                    <div className="edit-form">
                      <div className="form-field">
                        <span className="label-icon">📌 Subject:</span>
                        <input
                          type="text"
                          placeholder="📚 Subject"
                          value={selectedPost.subject || ''}
                          onChange={(e) =>
                            setSelectedPost((prev) => ({
                              ...prev,
                              subject: e.target.value,
                            }))
                          }
                          className="subject-input"
                        />
                      </div>
                      <div className="char-counter">
                        <span className={selectedPost.subject.length > 100 ? 'warning' : ''}>
                          {selectedPost.subject.length}/100
                        </span>
                      </div>

                      <div className="form-field">
                        <span className="label-icon">💭 Content:</span>
                        <textarea
                          value={selectedPost.content || ''}
                          onChange={(e) =>
                            setSelectedPost((prev) => ({
                              ...prev,
                              content: e.target.value,
                            }))
                          }
                          className="content-textarea"
                          placeholder="📝 Your knowledge content"
                          rows={15}
                        />
                      </div>
                      <div className="char-progress-container">
                        <div className="char-progress-bar">
                          <div
                            className="char-progress-fill"
                            style={{
                              width: `${charPercentage}%`,
                              background:
                                charPercentage > 90
                                  ? '#ef4444'
                                  : charPercentage > 70
                                  ? '#f59e0b'
                                  : '#10b981',
                            }}
                          ></div>
                        </div>
                        <span
                          className={`char-count-text ${charPercentage > 90 ? 'danger' : ''}`}
                        >
                          {charCount} / {maxChars} characters
                        </span>
                      </div>

                      <button
                        className="save-changes-btn"
                        onClick={handleUpdatePost}
                        disabled={charCount > maxChars}
                      >
                        💾 Save Changes
                      </button>
                    </div>
                  ) : (
                    <div className="view-content">
                      <div className="subject-section">
                        <h3>📚 Subject</h3>
                        <p className="subject-text">{selectedPost.subject}</p>
                      </div>

                      <div className="content-section">
                        <h3>📄 Content</h3>
                        <div className="content-text">
                          {selectedPost.content.split('\n').map((line, idx) => (
                            <p key={idx}>{line || '\u00A0'}</p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <CustomAlert
          visible={showDeleteAlert}
          message="Are you sure you want to delete this knowledge post? This action cannot be undone."
          onConfirm={() => {
            handleDeletePost(pendingDeleteId);
            setShowDeleteAlert(false);
            setPendingDeleteId(null);
          }}
          onCancel={() => {
            setShowDeleteAlert(false);
            setPendingDeleteId(null);
          }}
        />
      </div>
    </div>
  );
};

export default KnowledgeSharing;